import React, { useEffect, useMemo, useState } from "react";
import { ShieldCheck, Lock } from "lucide-react";
import { ButtonPrimary, ButtonGhost } from "./_shared";
import { HeroCityPill } from "./HeroCityPill";

const MEDIA = {
  hero_barrier: "/branded/hero-signature.png",
  hero_bg:
    "https://static.prod-images.emergentagent.com/jobs/9b82d871-4b50-41f8-8598-25731f623180/images/bd688771252d0408ab00aa6b67c94be9ef1b94376d1ffc634167affbaccb9508.png",
};

const HeroDiagnosticsCard = ({ label, value, sub, dot = true, testId }) => (
  <div data-testid={testId} className="bbc-glass px-4 py-3 min-w-0 sm:min-w-[160px]">
    <div className="flex items-center gap-2">
      {dot && <span className="w-1.5 h-1.5 rounded-full bg-[#B0FFD9] bbc-pulse" />}
      <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#71717A]">{label}</span>
    </div>
    <div className="mt-1.5 font-display text-white text-xl font-bold tracking-tight">{value}</div>
    {sub && <div className="font-mono text-[10px] text-[#71717A] mt-0.5">{sub}</div>}
  </div>
);

/* 9 telemetry cards rotating 3-at-a-time on the hero barriers */
const TELEMETRY_BADGES = [
  { eyebrow: "Live · Online", dot: "#00FF85", serial: "BBC-2417", title: "DLF Cyberhub · Lane 3",
    rows: [{ k: "Cycles", v: "12.4M", accent: "#00FF85" }, { k: "Last sync", v: "14s ago" }] },
  { eyebrow: "Diagnostic", dot: "#00FF85", serial: "BBC-2418", title: "Embassy Tech Sq · Lane 1",
    bar: { label: "Health", value: 98, color: "#00FF85" },
    rows: [{ k: "Motor", v: "OK", accent: "#00FF85" }, { k: "Temp", v: "34°C" }, { k: "Faults · 30d", v: "0", accent: "#00FF85" }] },
  { eyebrow: "AMC Scheduled", dot: "#B0FFD9", serial: "BBC-2419", title: "Mumbai Toll · Lane 4",
    rows: [{ k: "Next visit", v: "12 May", accent: "#B0FFD9" }, { k: "In", v: "18 days" }, { k: "Technician", v: "Verified", accent: "#00FF85", icon: ShieldCheck }] },
  { eyebrow: "Cycle Analytics", dot: "#00FF85", serial: "BBC-2421", title: "Phoenix Mall · Lane 2",
    rows: [{ k: "Today", v: "1,847", accent: "#00FF85" }, { k: "Peak hr", v: "6–7 PM" }, { k: "Lifetime", v: "8.6M" }] },
  { eyebrow: "Payment Lock", dot: "#FBBF24", serial: "BBC-2424", title: "Lodha Park · Tower B",
    rows: [{ k: "Buyer", v: "F-1404" }, { k: "Status", v: "Overdue 32d", accent: "#FBBF24" }, { k: "Access", v: "Locked", accent: "#FBBF24", icon: Lock }] },
  { eyebrow: "Fault Alert", dot: "#FB7185", serial: "BBC-2426", title: "Hyderabad Hub · Lane 2",
    rows: [{ k: "Fault", v: "Door sensor", accent: "#FB7185" }, { k: "Technician", v: "Dispatched", accent: "#B0FFD9" }, { k: "ETA", v: "38 min" }] },
  { eyebrow: "Fleet Overview", dot: "#B0FFD9", serial: "FLEET", title: "Pan-India · Live Map",
    rows: [{ k: "Online", v: "2,387", accent: "#00FF85" }, { k: "In service", v: "11" }, { k: "Faults", v: "2", accent: "#FBBF24" }] },
  { eyebrow: "Firmware OTA", dot: "#B0FFD9", serial: "BBC-2429", title: "Indiabulls Park · All Lanes",
    rows: [{ k: "Version", v: "v2.1.4" }, { k: "Status", v: "Pushed · 4h ago", accent: "#00FF85" }, { k: "Devices", v: "12 / 12" }] },
  { eyebrow: "Integration", dot: "#00FF85", serial: "BBC-2431", title: "Brigade Gateway · ANPR",
    rows: [{ k: "ANPR feed", v: "Live", accent: "#00FF85" }, { k: "RFID", v: "Online", accent: "#00FF85" }, { k: "Last event", v: "2s ago" }] },
];

const TELEMETRY_PER_VIEW = 3;
const TELEMETRY_GROUPS = Math.ceil(TELEMETRY_BADGES.length / TELEMETRY_PER_VIEW);
const TELEMETRY_INTERVAL_MS = 5200;

const DESKTOP_SLOTS = [
  { top: "6%",  left: "20%", delay: "0s" },
  { top: "12%", left: "56%", delay: "0.6s" },
  { top: "20%", left: "82%", delay: "1.2s" },
];

const MOBILE_SLOTS = [
  { top: "8%",  left: "30%", delay: "0s" },
  { top: "24%", left: "68%", delay: "0.6s" },
  { top: "42%", left: "40%", delay: "1.2s" },
];

const BadgeHeader = ({ eyebrow, serial, dot = "#00FF85", delay }) => (
  <div className="flex items-center justify-between px-3 pt-2.5 pb-2 border-b border-white/5">
    <div className="flex items-center gap-2">
      <span className="w-1.5 h-1.5 rounded-full bbc-pulse" style={{ background: dot, animationDelay: delay }} />
      <span className="font-mono text-[8px] uppercase tracking-[0.22em]" style={{ color: dot }}>{eyebrow}</span>
    </div>
    <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-white/85">{serial}</span>
  </div>
);

const BadgeKV = ({ row }) => {
  const Icon = row.icon;
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-white/45">{row.k}</span>
      <span className="inline-flex items-center gap-1 font-mono text-[10px] tracking-tight" style={{ color: row.accent || "#FFFFFF" }}>
        {Icon && <Icon size={9} />}
        {row.v}
      </span>
    </div>
  );
};

const HeroBarrierBadge = ({ badge, style, delay, testId, compact = false }) => {
  if (!badge) return null;
  return (
    <div data-testid={testId} className="absolute -translate-x-1/2 -translate-y-1/2 bbc-badge-in" style={style}>
      <span
        className={`absolute left-1/2 top-full -translate-x-1/2 w-px ${compact ? "h-8" : "h-20"} bg-gradient-to-b from-white/40 via-white/10 to-transparent`}
        aria-hidden
      />
      <div
        className="relative bg-[#0F1113]/90 backdrop-blur-md border border-white/15 shadow-2xl shadow-black/40"
        style={{
          animation: `bbcFloat 6s ease-in-out infinite`,
          animationDelay: delay,
          minWidth: compact ? "150px" : "210px",
          transform: compact ? "scale(0.82)" : undefined,
          transformOrigin: "center center",
        }}
      >
        <BadgeHeader eyebrow={badge.eyebrow} serial={badge.serial} dot={badge.dot} delay={delay} />
        <div className="px-3 py-2.5 space-y-1.5">
          <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-white/65 truncate">{badge.title}</div>

          {badge.bar && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-white/45">{badge.bar.label}</span>
                <span className="font-mono text-[10px]" style={{ color: badge.bar.color }}>{badge.bar.value}%</span>
              </div>
              <div className="relative h-1 bg-white/8 overflow-hidden">
                <div className="absolute inset-y-0 left-0" style={{ width: `${badge.bar.value}%`, background: badge.bar.color }} />
              </div>
            </div>
          )}

          {badge.rows.map((row, i) => (
            <BadgeKV key={i} row={row} />
          ))}
        </div>
      </div>
    </div>
  );
};

export const Hero = () => {
  const [group, setGroup] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setGroup((g) => (g + 1) % TELEMETRY_GROUPS), TELEMETRY_INTERVAL_MS);
    return () => clearInterval(t);
  }, []);

  const currentTrio = useMemo(() => {
    const start = group * TELEMETRY_PER_VIEW;
    return [0, 1, 2].map((i) => TELEMETRY_BADGES[(start + i) % TELEMETRY_BADGES.length]);
  }, [group]);

  return (
    <section
      id="top"
      data-testid="platform-hero"
      className="relative min-h-screen pt-24 md:pt-32 pb-16 md:pb-20 overflow-hidden"
    >
      <div className="absolute inset-0 -z-10">
        <img src={MEDIA.hero_bg} alt="" className="w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F1113] via-[#0F1113]/40 to-[#0F1113]" />
      </div>
      <div className="absolute inset-0 bbc-grid-bg opacity-30 -z-10" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center gap-3">
          <span className="w-8 h-px bg-[#00FF85]" />
          <span className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.25em] sm:tracking-[0.3em] text-[#00FF85]">
            Industry's First Traceable Barrier Systems
          </span>
        </div>

        <h1
          data-testid="platform-hero-heading"
          className="font-display mt-6 md:mt-8 text-white text-[2.6rem] leading-[1.02] sm:text-6xl lg:text-7xl xl:text-8xl font-black tracking-[-0.02em] sm:leading-[0.95] max-w-5xl"
        >
          Boom Barriers Built
          <br />
          Like <span className="text-[#00FF85]">Infrastructure.</span>
        </h1>

        <div className="mt-6 md:mt-8 max-w-3xl" data-testid="platform-hero-sub">
          <p className="font-display text-white text-2xl sm:text-3xl md:text-[2rem] lg:text-4xl font-bold tracking-[-0.01em] leading-[1.18]">
            <span className="text-[#00FF85]">Engineered.</span>{" "}
            <span className="text-[#00FF85]">Tested.</span>{" "}
            <span className="text-[#00FF85]">Tracked.</span>
            <br />
            <span className="text-white/85">From the factory to your phone.</span>
          </p>
        </div>

        <div className="mt-8 md:mt-10 flex flex-wrap gap-3">
          <ButtonPrimary href="#portfolio" testId="platform-hero-cta-primary">See Our Barriers</ButtonPrimary>
          <ButtonGhost lead="sales" source="hero" testId="platform-hero-cta-secondary">Talk to Sales</ButtonGhost>
        </div>

        <HeroCityPill />

        {/* Mobile hero product image */}
        <div className="lg:hidden mt-10 relative" data-testid="platform-hero-mobile-image">
          <div className="relative aspect-[4/5] sm:aspect-[3/2] overflow-hidden border border-white/10 bg-[#0F1113]">
            <img
              src={MEDIA.hero_barrier}
              alt="BBC signature branded boom barrier cabinet — QR-traceable, dual-LED, Made in India"
              className="absolute inset-0 w-full h-full object-cover object-right"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F1113]/70 via-[#0F1113]/10 to-transparent pointer-events-none" />

            {MOBILE_SLOTS.map((slot, i) => {
              const badge = currentTrio[i];
              if (!badge) return null;
              return (
                <HeroBarrierBadge
                  key={`m-${group}-${i}`}
                  badge={badge}
                  style={{ top: slot.top, left: slot.left }}
                  delay={slot.delay}
                  testId={`hero-telemetry-m-${i + 1}`}
                  compact
                />
              );
            })}
          </div>
          <div className="mt-3 flex items-center gap-2" data-testid="hero-telemetry-dots-mobile">
            {Array.from({ length: TELEMETRY_GROUPS }).map((_, i) => (
              <span key={i} className={`h-1 transition-all ${i === group ? "w-6 bg-[#00FF85]" : "w-3 bg-white/15"}`} />
            ))}
            <span className="ml-1 font-mono text-[9px] uppercase tracking-[0.22em] text-[#52525B]">
              {TELEMETRY_BADGES.length} live signals
            </span>
          </div>
        </div>

        {/* Diagnostics cards */}
        <div className="mt-12 md:mt-20 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-4xl">
          <HeroDiagnosticsCard testId="hero-stat-uptime" label="Field Uptime" value="99.94%" sub="Across deployments" />
          <HeroDiagnosticsCard testId="hero-stat-deployments" label="Barriers Tracked" value="2,400+" sub="Across India" />
          <HeroDiagnosticsCard testId="hero-stat-cycles" label="Cycles Logged" value="47.2M" sub="Lifetime" />
          <HeroDiagnosticsCard testId="hero-stat-alerts" label="Critical Faults" value="0" sub="In last 30 days" dot={false} />
        </div>

        {/* Desktop hero product image */}
        <div className="hidden lg:block absolute right-0 bottom-0 w-[48%] max-w-[760px] pointer-events-none">
          <div className="relative">
            <img
              src={MEDIA.hero_barrier}
              alt="BBC signature branded boom barrier cabinet — QR-traceable, dual-LED, Made in India"
              className="w-full h-auto opacity-95"
            />
            <div className="absolute inset-0 bg-gradient-to-l from-transparent to-[#0F1113]/30" />

            {DESKTOP_SLOTS.map((slot, i) => {
              const badge = currentTrio[i];
              if (!badge) return null;
              return (
                <HeroBarrierBadge
                  key={`d-${group}-${i}`}
                  badge={badge}
                  style={{ top: slot.top, left: slot.left }}
                  delay={slot.delay}
                  testId={`hero-telemetry-${i + 1}`}
                />
              );
            })}

            <div className="absolute bottom-4 left-4 flex items-center gap-2" data-testid="hero-telemetry-dots-desktop">
              {Array.from({ length: TELEMETRY_GROUPS }).map((_, i) => (
                <span key={i} className={`h-1 transition-all ${i === group ? "w-6 bg-[#00FF85]" : "w-3 bg-white/20"}`} />
              ))}
              <span className="ml-1 font-mono text-[9px] uppercase tracking-[0.22em] text-[#71717A]">
                {TELEMETRY_BADGES.length} live signals
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
